import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import MockDataManager from '../Testing/MockDataManager';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService } from '../../lib/college-journey-service';
import { 
  Brain, 
  Users, 
  Settings, 
  ChevronLeft, 
  Play, 
  UserPlus,
  Eye,
  TestTube,
  Sparkles,
  RefreshCw,
  User,
  School,
  Trash2,
  FileText,
  ArrowLeft,
  Database, 
  Plus,
  Minus,
  MessageSquare,
  MessageSquareMore,
  CheckCircle,
  Code,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';

// API Request Preview Component
const APIRequestPreview = ({ 
  selectedProfile, 
  selectedResponseSet, 
  responseSetType, 
  selectedPreferenceSet, 
  mockCollegePreferences, 
  generateAPIRequestPreview 
}) => {
  const [apiPreview, setApiPreview] = useState(null);
  const [showFullRequest, setShowFullRequest] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPreview = async () => {
      if (!selectedProfile) {
        setApiPreview(null);
        return;
      }
      
      setLoading(true);
      try {
        const preview = await generateAPIRequestPreview();
        setApiPreview(preview);
      } catch (error) {
        console.error('Error loading API preview:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadPreview();
  }, [selectedProfile, selectedResponseSet, responseSetType, selectedPreferenceSet, mockCollegePreferences, generateAPIRequestPreview]);

  if (!selectedProfile || loading) {
    return (
      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <div className="flex items-center gap-2 text-slate-600">
          <Code className="w-4 h-4" />
          <span className="text-sm font-medium">
            {loading ? 'Loading API preview...' : 'API Request Preview'}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {loading ? 'Fetching data...' : 'Select profile and preferences to see API request preview'}
        </p>
      </div>
    );
  }

  if (!apiPreview) {
    return (
      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Unable to generate API preview</span>
        </div>
        <p className="text-xs text-red-500 mt-1">Check console for details</p>
      </div>
    );
  }

  const { preview } = apiPreview;
  const hasPersonalizedData = preview.strategicContextLength > 0 && preview.synthesisInstructionsLength > 0;
  const hasCompleteData = preview.hasStudentProfile && preview.hasParentContext && hasPersonalizedData;

  return (
    <div className="mt-6 p-4 bg-slate-900/95 backdrop-blur-sm rounded-2xl border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-slate-300" />
          <span className="text-sm font-semibold text-white">API Request Preview</span>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${
          hasCompleteData 
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
            : hasPersonalizedData
            ? 'bg-azure-500/20 text-azure-400 border border-azure-500/30'
            : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
        }`}>
          {hasCompleteData ? '✅ Complete Enhanced Data' : hasPersonalizedData ? '🔵 Partial Data' : '⚠️ Generic Fallback'}
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-slate-400 mb-1">Endpoint</div>
            <div className="text-sm text-white font-mono">POST {apiPreview.url}</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-slate-400 mb-1">Response Data</div>
            <div className="text-sm text-white">
              {preview.actualResponsesCount} actual + {preview.mockResponsesCount} mock
              <div className="text-xs text-slate-400">= {preview.responsesCount} total responses</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-white">{preview.strategicContextLength}</div>
            <div className="text-xs text-slate-400">Strategic Context chars</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-white">{preview.synthesisInstructionsLength}</div>
            <div className="text-xs text-slate-400">Synthesis Instructions chars</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-white">{preview.initialQuestionsCount}</div>
            <div className="text-xs text-slate-400">Initial Questions</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-white">{preview.parentObservationsCount}</div>
            <div className="text-xs text-slate-400">Parent Observations</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-slate-400 mb-1">Student Profile</div>
            <div className="text-sm text-white">
              {preview.hasStudentProfile ? preview.studentProfileSummary : 'No profile data'}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-slate-400 mb-1">Parent Context</div>
            <div className="text-sm text-white">
              {preview.hasParentContext ? `${preview.parentObservationsCount} observations` : 'No parent data'}
            </div>
          </div>
        </div>

        {(hasPersonalizedData || hasCompleteData) && (
          <div className="space-y-2">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-2">Strategic Context Preview</div>
              <div className="text-sm text-slate-300 font-mono leading-relaxed">
                {preview.strategicContextPreview}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-2">Synthesis Instructions Preview</div>
              <div className="text-sm text-slate-300 font-mono leading-relaxed">
                {preview.synthesisInstructionsPreview}
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => setShowFullRequest(!showFullRequest)}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors text-sm text-slate-300"
        >
          {showFullRequest ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showFullRequest ? 'Hide' : 'Show'} Full Request Body
        </button>

        {showFullRequest && (
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-slate-400 mb-2">Full Request JSON</div>
            <pre className="text-xs text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap break-words">
              {JSON.stringify(apiPreview.body, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

const ParentTestingHub = () => {
  const navigate = useNavigate();
  const { familyUsers, switchUser, currentUser } = useFamilyContext();
  const [showMockDataManager, setShowMockDataManager] = useState(false);
  
  // Check if current user has invalid UUID and clear it
  useEffect(() => {
    if (currentUser?.isMock && currentUser?.id) {
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(currentUser.id);
      if (!isValidUUID) {
        console.warn('Current mock user has invalid UUID, switching back to real user');
        const realUser = localStorage.getItem('real-user-context');
        if (realUser) {
          switchUser(JSON.parse(realUser));
          localStorage.removeItem('real-user-context');
        } else {
          window.location.href = '/college';
        }
      }
    }
  }, [currentUser, switchUser]);
  
  const [mockProfiles, setMockProfiles] = useState(() => {
    try {
      const saved = localStorage.getItem('parent-testing-mock-profiles');
      if (saved) {
        const profiles = JSON.parse(saved);
        // Filter out profiles with invalid UUIDs (older mock profiles)
        const validProfiles = profiles.filter(profile => {
          const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profile.id);
          if (!isValidUUID) {
            console.log('Removing invalid mock profile with non-UUID ID:', profile.id);
          }
          return isValidUUID;
        });
        return validProfiles;
      }
      return [];
    } catch {
      return [];
    }
  });
  
  const [showCreateMock, setShowCreateMock] = useState(false);
  const [newMockProfile, setNewMockProfile] = useState({
    name: '',
    grade: '',
    profileType: '',
    description: ''
  });
  
  const [showCollegePreferences, setShowCollegePreferences] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [mockCollegePreferences, setMockCollegePreferences] = useState({
    targetSchools: '',
    preferredMajors: '',
    locationPreferences: '',
    campusSize: '',
    specialInterests: '',
    careerGoals: '',
    collegeImportanceFactors: '',
    concernsOrWorries: ''
  });
  
  const [savedPreferenceSets, setSavedPreferenceSets] = useState([]);
  const [selectedPreferenceSet, setSelectedPreferenceSet] = useState(null);
  const [preferenceMode, setPreferenceMode] = useState(null); // 'new' or 'saved'
  const [showSavePreferenceOption, setShowSavePreferenceOption] = useState(false);
  const [preferenceName, setPreferenceName] = useState('');
  
  const [savedResponseSets, setSavedResponseSets] = useState([]);
  const [selectedResponseSet, setSelectedResponseSet] = useState(null);
  const [responseSetType, setResponseSetType] = useState(null); // '1-8' or '1-15'

  // Save mock profiles to localStorage
  useEffect(() => {
    localStorage.setItem('parent-testing-mock-profiles', JSON.stringify(mockProfiles));
  }, [mockProfiles]);
  
  // Load saved preference sets and response sets when profile changes
  useEffect(() => {
    const loadSavedData = async () => {
      if (!currentUser) return;
      
      try {
        // Load saved preference sets - filter by student_id to match selected profile
        const selectedStudentId = selectedProfile?.id || currentUser.id;
        
        const { data: prefData } = await supabase
          .from('mock_college_preferences')
          .select('*')
          .eq('student_id', selectedStudentId)
          .order('created_at', { ascending: false });
        
        if (prefData) {
          setSavedPreferenceSets(prefData);
        }
        
        // Load saved response sets - filter by student_id to match selected profile
        console.log('🔍 Loading response sets for student:', {
          selectedProfileId: selectedProfile?.id,
          selectedProfileName: selectedProfile?.name,
          currentUserId: currentUser.id,
          currentUserName: currentUser.name,
          effectiveStudentId: selectedStudentId
        });

        const { data: respData, error: respError } = await supabase
          .from('mock_question_responses')
          .select('*')
          .eq('student_id', selectedStudentId)
          .order('created_at', { ascending: false });

        console.log('🔍 Response sets query result:', {
          data: respData,
          error: respError,
          count: respData?.length || 0
        });
        
        if (respData) {
          setSavedResponseSets(respData);
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    };
    
    loadSavedData();
  }, [currentUser, selectedProfile]);

  // Removed predefined mock profiles as requested

  // Generate a valid UUID v4 for mock profiles
  const generateMockUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleCreateMockProfile = () => {
    const newProfile = {
      ...newMockProfile,
      id: generateMockUUID(), // Use proper UUID format
      isMock: true,
      createdAt: new Date().toISOString()
    };
    
    setMockProfiles([...mockProfiles, newProfile]);
    setNewMockProfile({ name: '', grade: '', profileType: '', description: '' });
    setShowCreateMock(false);
  };

  const handleSelectProfile = async (profile, isRealProfile = false) => {
    setSelectedProfile({ ...profile, isRealProfile });
    
    // Store the selected student ID in localStorage for database queries
    if (isRealProfile) {
      // For real profiles, we need to get their auth_user_id for database queries
      // The profile.id from familyUsers is their family member ID, but we need auth_user_id
      const authUserIdToStore = profile.authUserId || profile.auth_user_id || profile.id;
      localStorage.setItem('college-selected-student-id', authUserIdToStore);
      localStorage.setItem('college-selected-student-name', profile.displayName || profile.name);
      console.log('🔍 Stored student auth ID in localStorage:', authUserIdToStore);
    } else {
      // For mock profiles, store the mock profile ID
      localStorage.setItem('college-selected-student-id', profile.id);
      localStorage.setItem('college-selected-student-name', profile.name);
      console.log('🔍 Stored mock profile ID in localStorage:', profile.id);
    }
    
    if (isRealProfile) {
      // For real profiles, load mock data sets created specifically for this student
      console.log('🔍 Loading mock data for student:', profile.name, profile.id);
      
      try {
        const userIdForDb = currentUser.authUserId || currentUser.auth_user_id;
        
        // Load mock preference sets for this specific student
        const { data: prefData } = await supabase
          .from('mock_college_preferences')
          .select('*')
          .eq('created_by', userIdForDb)
          .eq('student_id', profile.id)
          .order('created_at', { ascending: false });
        
        console.log('🔍 Found student-specific preference sets:', prefData?.length || 0);
        setSavedPreferenceSets(prefData || []);
        
        // Auto-select the most recent preference set if available
        if (prefData && prefData.length > 0) {
          setSelectedPreferenceSet(prefData[0]);
          setPreferenceMode('saved');
          console.log('✅ Auto-selected most recent preference set:', prefData[0].name);
        }
        
        // Load mock response sets for this specific student
        const { data: respData } = await supabase
          .from('mock_question_responses')
          .select('*')
          .eq('created_by', userIdForDb)
          .eq('student_id', profile.id)
          .order('created_at', { ascending: false });
        
        console.log('🔍 Found student-specific response sets:', respData?.length || 0);
        setSavedResponseSets(respData || []);
        
        // Auto-select the most recent response set if available
        if (respData && respData.length > 0) {
          setSelectedResponseSet(respData[0]);
          setResponseSetType('1-8'); // Default to 1-8 mode
          console.log('✅ Auto-selected most recent response set:', respData[0].name);
        }
        
      } catch (error) {
        console.error('Error loading student-specific mock data:', error);
        // Fall back to general data
        setSavedPreferenceSets([]);
        setSavedResponseSets([]);
      }
    } else {
      // For mock profiles, check if we have saved preference sets
      const userIdForDb = currentUser.authUserId || currentUser.auth_user_id;
      
      try {
        // Load general preference sets (not student-specific)
        const { data: prefData } = await supabase
          .from('mock_college_preferences')
          .select('*')
          .eq('created_by', userIdForDb)
          .is('student_id', null)
          .order('created_at', { ascending: false });
        
        if (prefData && prefData.length > 0) {
          setSavedPreferenceSets(prefData);
          setSelectedPreferenceSet(prefData[0]);
          setPreferenceMode('saved');
          console.log('✅ Auto-selected most recent general preference set:', prefData[0].name);
        }
        
        // Load general response sets
        const { data: respData } = await supabase
          .from('mock_question_responses')
          .select('*')
          .eq('created_by', userIdForDb)
          .is('student_id', null)
          .order('created_at', { ascending: false });
        
        if (respData && respData.length > 0) {
          setSavedResponseSets(respData);
          setSelectedResponseSet(respData[0]);
          setResponseSetType('1-8');
          console.log('✅ Auto-selected most recent general response set:', respData[0].name);
        }
      } catch (error) {
        console.error('Error loading general mock data:', error);
      }
      
      // Load existing mock preferences if they exist
      const existingPrefs = localStorage.getItem('mock-college-preferences');
      if (existingPrefs) {
        setMockCollegePreferences(JSON.parse(existingPrefs));
      }
    }
  };

  const handleStartAssessment = () => {
    if (!selectedProfile) return;
    
    // Save the preferences (either new or from selected set)
    const preferencesToSave = selectedPreferenceSet 
      ? selectedPreferenceSet.preferences 
      : mockCollegePreferences;
    
    localStorage.setItem('mock-college-preferences', JSON.stringify(preferencesToSave));
    
    // Save selected response set if any
    if (selectedResponseSet) {
      localStorage.setItem('mock-response-set', JSON.stringify({
        responseSet: selectedResponseSet,
        type: responseSetType // '1-8' or '1-15'
      }));
    } else {
      localStorage.removeItem('mock-response-set');
    }
    
    // Store testing mode flags
    localStorage.setItem('testing-mode', 'true');
    
    if (selectedProfile.isRealProfile) {
      // For real profiles, store the current parent user context before switching
      console.log('🔄 Starting assessment with real profile, storing current parent context:', currentUser);
      localStorage.setItem('real-user-context', JSON.stringify(currentUser));
      localStorage.setItem('testing-profile-id', selectedProfile.id);
      switchUser(selectedProfile);
      // Wait for context to update before navigating (increased delay for proper context switching)
      setTimeout(() => {
        navigate('/college/assessment');
      }, 500);
    } else {
      // Create a temporary mock user context
      const mockUser = {
        id: selectedProfile.id,
        name: selectedProfile.name,
        displayName: selectedProfile.name,
        isMock: true,
        mockProfile: selectedProfile
      };
      
      // Store current real user for easy switching back
      console.log('🔄 Starting assessment with mock profile, storing current parent context:', currentUser);
      localStorage.setItem('real-user-context', JSON.stringify(currentUser));
      
      // Switch to mock user and navigate to assessment
      switchUser(mockUser);
      // Wait for context to update before navigating (increased delay for proper context switching)
      setTimeout(() => {
        navigate('/college/assessment');
      }, 500);
    }
  };

  const handleSwitchBackToReal = async () => {
    try {
      const realUser = localStorage.getItem('real-user-context');
      if (realUser) {
        const parsedRealUser = JSON.parse(realUser);
        console.log('🔄 Restoring real user context in ParentTestingHub:', parsedRealUser);
        
        // Switch user and wait for state to update
        switchUser(parsedRealUser);
        
        // Clear the stored real user context
        localStorage.removeItem('real-user-context');
        
        // Return the parsed user for immediate use
        return parsedRealUser;
      }
      // Clear testing mode flags
      localStorage.removeItem('testing-mode');
      localStorage.removeItem('testing-profile-id');
      localStorage.removeItem('mock-college-preferences');
      
      return null;
    } catch (error) {
      console.error('Error switching back to real user:', error);
      return null;
    }
  };

  const clearTestingData = async (profileId) => {
    try {
      // Clear all testing data for this profile
      const keysToRemove = [
        'selectedStudentUserId',
        'college-selected-student-id',
        // Add any other keys that might store testing data
      ];
      
      keysToRemove.forEach(key => {
        const value = localStorage.getItem(key);
        if (value === profileId) {
          localStorage.removeItem(key);
        }
      });
      
      // You might also want to clear specific database entries for testing
      // This would depend on your database structure
      console.log(`Cleared testing data for profile: ${profileId}`);
      
      // Show success message
      alert('Testing data cleared successfully!');
    } catch (error) {
      console.error('Error clearing testing data:', error);
      alert('Error clearing testing data. Please try again.');
    }
  };

  const deleteMockProfile = (profileId) => {
    setMockProfiles(mockProfiles.filter(p => p.id !== profileId));
  };

  // Generate API request preview for testing using localStorage-based ID resolution
  const generateAPIRequestPreview = async () => {
    if (!selectedProfile) return null;

    try {
      console.log('🔍 PREVIEW: Loading foundation data with testing-aware ID resolution');
      console.log('🔍 PREVIEW: Selected profile:', selectedProfile.id, selectedProfile.name);
      
      // Check if we're in testing mode by comparing user IDs
      const savedStudentId = localStorage.getItem('college-selected-student-id');
      const currentUserAuthId = currentUser?.auth_user_id || currentUser?.authUserId;
      const isTestingMode = savedStudentId && savedStudentId !== currentUserAuthId;
      
      console.log('🔍 PREVIEW: Testing mode detection:', {
        savedStudentId,
        currentUserAuthId,
        isTestingMode
      });
      
      let studentUserId;
      
      if (isTestingMode) {
        // Testing mode: Use the selected student's auth_user_id from localStorage
        studentUserId = savedStudentId;
        console.log('🔍 PREVIEW: Using student auth_user_id from localStorage (testing):', studentUserId);
      } else {
        // Normal mode: Use the current user's auth_user_id
        studentUserId = currentUserAuthId;
        console.log('🔍 PREVIEW: Using current user auth_user_id (normal):', studentUserId);
        
        if (!studentUserId) {
          console.error('❌ PREVIEW: No auth_user_id found on current user');
          return null;
        }
      }
      
      // Query database directly with the stored student ID
      console.log('🔍 PREVIEW: Loading academic profile data...');
      const { data: profileData, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', studentUserId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ PREVIEW: Error loading student data:', error);
        return null;
      }

      if (!profileData) {
        console.log('❌ PREVIEW: No profile data found for studentUserId:', studentUserId);
        return null;
      }

      console.log('✅ PREVIEW: Loaded profile data:', {
        strategic_context_length: profileData.strategic_context_summary?.length || 0,
        synthesis_instructions_length: profileData.synthesis_instructions?.length || 0,
        initial_questions_count: profileData.initial_question_set?.length || 0,
        has_parent_observations: !!(profileData.parent_observation_easy_subject),
        profile_id: profileData.id
      });

      // Also get saved assessment responses for the student
      const { data: savedResponses } = await supabase
        .from('assessment_responses')
        .select('responses')
        .eq('user_id', selectedProfile.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('🔍 PREVIEW: Saved assessment responses:', savedResponses);

      // Transform data using exact same pattern as ConversationalAssessment (lines 565-581)
      const studentProfile = {
        academicInterests: profileData.academic_interests || [],
        careerGoals: profileData.career_goals || '',
        learningStyle: profileData.learning_style || '',
        academicStrengths: profileData.academic_strengths || [],
        uniqueQualities: profileData.unique_qualities || '',
        studentSelfAssessment: profileData.student_self_assessment || '',
        // Add college preferences in the format the API expects
        collegePreferences: {
          intendedMajors: profileData.intended_major ? [profileData.intended_major] : [],
          keyCriteria: profileData.career_goals || '',
          studentUniqueInsight: profileData.unique_qualities || '',
          // Add fallback from intended_career field
          intendedCareer: profileData.intended_career || ''
        }
      };

      // Transform parent observations exactly like ConversationalAssessment (lines 587-599)
      const parentObservations = {
        easySubject: profileData.parent_observation_easy_subject || '',
        growth: profileData.parent_observation_growth || '',
        poorFit: profileData.parent_observation_poor_fit || '',
        surprises: profileData.parent_observation_surprises || '',
        ageLimit: profileData.parent_observation_age_limit || '',
        rulesQuestioned: profileData.parent_observation_rules_questioned || '',
        wrongLevel: profileData.parent_observation_wrong_level || '',
        selfTaught: profileData.parent_observation_self_taught || '',
        obsessions: profileData.parent_observation_obsessions || '',
        unusualInterests: profileData.parent_observation_unusual_interests || '',
        hiddenQualities: profileData.parent_observation_hidden_qualities || ''
      };

      // Check if we have meaningful parent data (line 602)
      const hasParentData = Object.values(parentObservations).some(value => value && value.trim());
      let loadedParentContext = null;
      if (hasParentData) {
        loadedParentContext = parentObservations;
        console.log('✅ PREVIEW: Loaded parent observations:', parentObservations);
      }

      // Check for 6-phase strategic assessment data exactly like ConversationalAssessment (lines 611-620)
      const hasStrategicContext = profileData.strategic_context_summary && profileData.strategic_context_summary.trim();
      const hasInitialQuestions = profileData.initial_question_set && profileData.initial_question_set.length > 0;
      const hasSynthesisInstructions = profileData.synthesis_instructions && profileData.synthesis_instructions.trim();
      
      console.log('🔍 PREVIEW: Phase completion status:', {
        phase2_strategic_context: hasStrategicContext,
        phase3_initial_questions: hasInitialQuestions, 
        phase5_synthesis_instructions: hasSynthesisInstructions,
        assessment_phase: profileData.assessment_phase,
        strategic_context_length: profileData.strategic_context_summary?.length || 0,
        synthesis_instructions_length: profileData.synthesis_instructions?.length || 0
      });

      // Enhanced assessment detection like ConversationalAssessment (lines 624-625)
      const isEnhanced = hasSynthesisInstructions || hasStrategicContext || hasInitialQuestions;
      console.log('✅ PREVIEW: Enhanced assessment data found:', isEnhanced);

      // Get actual student responses combined with mock responses
      const actualStudentResponses = savedResponses?.responses || {};
      let finalResponses = { ...actualStudentResponses };
      if (selectedResponseSet && selectedResponseSet.responses) {
        finalResponses = { ...finalResponses, ...selectedResponseSet.responses };
        console.log('🔄 PREVIEW: Applied mock response overlay:', Object.keys(selectedResponseSet.responses).length, 'mock responses');
      }

      // Get preferences (either new or from selected set)
      const preferencesToUse = selectedPreferenceSet 
        ? selectedPreferenceSet.preferences 
        : mockCollegePreferences;

      // Build the complete API request exactly like ConversationalAssessment generateInsights() function
      const apiRequest = {
        action: 'generate-conversational-insights',
        responses: finalResponses,
        patternScores: {}, // Would be calculated from responses in real assessment
        studentProfile,
        parentContext: loadedParentContext,
        conversationHistory: [],
        // Enhanced assessment data (lines 654-657)
        strategicContext: profileData.strategic_context_summary || '',
        synthesisInstructions: profileData.synthesis_instructions || '',
        initialQuestions: profileData.initial_question_set || [],
        isEnhancedAssessment: isEnhanced,
        assessmentId: null,
        userId: selectedProfile.id,
        studentName: selectedProfile.displayName || selectedProfile.name
      };

      console.log('📋 PREVIEW: Generated comprehensive API request preview:', {
        responsesCount: Object.keys(finalResponses).length,
        mockResponsesApplied: selectedResponseSet ? Object.keys(selectedResponseSet.responses || {}).length : 0,
        strategicContextLength: profileData.strategic_context_summary?.length || 0,
        synthesisInstructionsLength: profileData.synthesis_instructions?.length || 0,
        initialQuestionsCount: profileData.initial_question_set?.length || 0,
        hasStudentProfile: !!studentProfile.careerGoals,
        hasParentContext: !!loadedParentContext,
        isEnhanced
      });

      return {
        url: '/api/anthropic/analysis',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: apiRequest,
        preview: {
          responsesCount: Object.keys(finalResponses).length,
          actualResponsesCount: Object.keys(actualStudentResponses).length,
          mockResponsesCount: selectedResponseSet ? Object.keys(selectedResponseSet.responses || {}).length : 0,
          strategicContextLength: profileData.strategic_context_summary?.length || 0,
          synthesisInstructionsLength: profileData.synthesis_instructions?.length || 0,
          initialQuestionsCount: profileData.initial_question_set?.length || 0,
          enhancedMode: isEnhanced,
          strategicContextPreview: profileData.strategic_context_summary?.substring(0, 200) + '...' || 'UNDEFINED',
          synthesisInstructionsPreview: profileData.synthesis_instructions?.substring(0, 200) + '...' || 'UNDEFINED',
          hasStudentProfile: !!studentProfile.careerGoals,
          hasParentContext: !!loadedParentContext,
          hasPreferences: Object.keys(preferencesToUse || {}).length > 0,
          selectedResponseSetName: selectedResponseSet?.name || 'None - Manual responses',
          studentProfileSummary: `Career: ${studentProfile.careerGoals}, Major: ${profileData.intended_major}`,
          parentObservationsCount: loadedParentContext ? Object.values(loadedParentContext).filter(v => v && v.trim()).length : 0
        }
      };
    } catch (error) {
      console.error('Error generating comprehensive API preview:', error);
      return null;
    }
  };

  const saveMockCollegePreferences = () => {
    // Save mock college preferences to localStorage for testing
    localStorage.setItem('mock-college-preferences', JSON.stringify(mockCollegePreferences));
    setShowCollegePreferences(false);
    
    // Show option to save as preset
    if (preferenceMode === 'new') {
      setShowSavePreferenceOption(true);
    }
  };
  
  const savePreferenceSet = async () => {
    if (!preferenceName.trim()) return;
    
    try {
      // Use authUserId for database operations that reference auth users
      const userIdForDb = currentUser.authUserId || currentUser.auth_user_id;
      
      console.log('🔍 Saving with user IDs:', {
        'currentUser.id': currentUser.id,
        'currentUser.authUserId': currentUser.authUserId,
        'currentUser.auth_user_id': currentUser.auth_user_id,
        'userIdForDb': userIdForDb,
        'selectedProfile.id': selectedProfile?.id
      });
      
      const { error } = await supabase
        .from('mock_college_preferences')
        .insert({
          name: preferenceName.trim(),
          description: `Preferences for ${selectedProfile?.displayName || selectedProfile?.name || 'testing'}`,
          preferences: mockCollegePreferences,
          created_by: userIdForDb,
          student_id: selectedProfile?.isRealProfile ? selectedProfile.id : null
        });
      
      if (error) {
        console.error('Error saving preference set:', error);
        alert('Error saving preference set');
      } else {
        alert('Preference set saved successfully!');
        setPreferenceName('');
        setShowSavePreferenceOption(false);
        // Reload saved preference sets
        const { data } = await supabase
          .from('mock_college_preferences')
          .select('*')
          .eq('created_by', userIdForDb)
          .order('created_at', { ascending: false });
        if (data) {
          setSavedPreferenceSets(data);
        }
      }
    } catch (error) {
      console.error('Error saving preference set:', error);
      alert('Error saving preference set');
    }
  };

  // Step refs for autoscroll
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);

  // Scroll to a step
  const scrollToStep = (ref) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // When profile is selected, scroll to Step 2
  useEffect(() => {
    if (selectedProfile) {
      setTimeout(() => scrollToStep(step2Ref), 200);
    }
  }, [selectedProfile]);

  // When preferences are set, scroll to Step 3
  useEffect(() => {
    if (
      selectedProfile &&
      ((preferenceMode === 'saved' && selectedPreferenceSet) ||
        (preferenceMode === 'new' && localStorage.getItem('mock-college-preferences')))
    ) {
      setTimeout(() => scrollToStep(step3Ref), 200);
    }
  }, [selectedProfile, preferenceMode, selectedPreferenceSet]);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Main container: reduced horizontal padding, responsive */}
      <div className="max-w-7xl mx-auto px-4 md:px-20 py-8 md:py-10">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={async () => {
              // Always restore real user context when going back
              await handleSwitchBackToReal();
              // Use replace to avoid navigation issues
              window.location.href = '/college';
            }}
            className="flex items-center gap-2 px-3 py-1.5 text-stone-600 hover:text-teal-600 bg-white/80 backdrop-blur-sm border border-stone-200 rounded-lg font-medium transition-all duration-150 hover:bg-white/95 hover:-translate-x-0.5 mb-8"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to College Journey
          </button>
          {/* Header row: reduced margin, smaller heading */}
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-2 leading-tight tracking-tight">Testing Hub</h1>
              <p className="text-base md:text-lg text-stone-500 font-normal">Test the 6-phase strategic assessment framework</p>
            </div>
            <button
              onClick={() => setShowMockDataManager(true)}
              className="bg-magenta-500 text-white px-4 py-2 rounded-lg font-semibold text-xs md:text-sm cursor-pointer transition-all duration-150 shadow-[0_2px_8px_rgba(233,30,99,0.2)] hover:bg-magenta-600 hover:-translate-y-0.5 hover:shadow-[0_4px_16px_rgba(233,30,99,0.3)]"
            >
              <Database className="w-4 h-4 inline mr-2" />
              Mock Data Manager
            </button>
          </div>
          {/* Testing as mock user alert: reduced margin/padding */}
          {currentUser?.isMock && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-600" />
                  <span className="text-xs md:text-sm font-medium text-amber-800">
                    Currently testing as: {currentUser.name}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    await handleSwitchBackToReal();
                    // Force a page reload to ensure clean state
                    window.location.reload();
                  }}
                  className="text-xs md:text-sm text-amber-700 hover:text-amber-800 font-medium"
                >
                  Switch Back to Real Profile
                </button>
              </div>
            </div>
          )}
        </div>
        {/* How to Test: tighter spacing, smaller text */}
        <div className="mb-8"> {/* reduced mb-10 to mb-8 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4"> {/* reduced gap */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-teal-200">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-[0_2px_8px_rgba(38,166,154,0.2)]">
                1
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">Choose Profile</h3>
              <p className="text-sm text-stone-500 leading-relaxed">Select kid or create a mock profile</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-teal-200">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-[0_2px_8px_rgba(38,166,154,0.2)]">
                2
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">Set Preferences</h3>
              <p className="text-sm text-stone-500 leading-relaxed">Configure mock college preferences</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-teal-200">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-[0_2px_8px_rgba(38,166,154,0.2)]">
                3
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">Run & Repeat</h3>
              <p className="text-sm text-stone-500 leading-relaxed">Test the assessment</p>
            </div>
          </div>
        </div>

        {/* Step 1: Select Profile */}
        <div className="mb-10" ref={step1Ref}> {/* reduced mb-16 to mb-10, add ref */}
          <div className="flex items-center justify-between mb-6"> {/* reduced mb-10 to mb-6 */}
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Step 1: Select Profile to Test</h2> {/* reduced text size */}
            {selectedProfile && (
              <button
                onClick={() => setSelectedProfile(null)}
                className="flex items-center gap-2 px-2 py-1 text-coral-500 hover:text-coral-600 text-xs font-semibold transition-colors duration-150"
              >
                <ArrowLeft className="w-3 h-3" />
                Clear Selection
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8"> {/* reduced gap-16 to gap-8 */}
            {/* Real Student Profiles */}
            <div className="lg:col-span-2">
              <h3 className="text-base font-semibold text-slate-900 mb-2">Real Student Profiles</h3> {/* smaller text */}
              {!familyUsers?.filter(user => !user.isParent).length ? (
                <div className="bg-white rounded-xl p-6 border border-slate-200 text-center">
                  <Users className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">No student profiles found</h4>
                  <p className="text-slate-600 text-sm mb-4">Add family members to test with real student profiles</p>
                  <button
                    onClick={() => navigate('/profile-management')}
                    className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm"
                  >
                    Manage Family Members
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* was space-y-3, now grid for 2x2 layout on desktop */}
                  {familyUsers.filter(user => !user.isParent).map(student => (
                    <button
                      key={student.id}
                      type="button"
                      onClick={() => handleSelectProfile(student, true)}
                      className={`w-full text-left bg-white border rounded-xl p-4 transition-all duration-200 relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2
                        ${selectedProfile?.id === student.id ? 'border-teal-500 ring-2 ring-teal-300 bg-teal-50' : 'border-stone-200 hover:border-teal-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]'}`}
                      aria-pressed={selectedProfile?.id === student.id}
                      tabIndex={0}
                    >
                      {/* Top gradient bar (hidden by default, shown on hover) */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900 mb-0.5 tracking-tight">{student.displayName || student.name}</h3>
                          <div className="text-xs text-stone-500 font-medium">Real Profile • Grade {student.grade || 'N/A'}</div>
                        </div>
                        <div className="bg-teal-50 text-teal-700 px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide">Testing Mode</div>
                      </div>
                      {/* Selected checkmark overlay */}
                      {selectedProfile?.id === student.id && (
                        <span className="absolute top-3 right-3 bg-teal-500 text-white rounded-full p-1 shadow"><CheckCircle className="w-4 h-4" /></span>
                      )}
                      <div className="flex gap-2 items-center mt-3">
                        {/* Remove Select button, keep Clear Data button */}
                        <div className="flex-1"></div>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); clearTestingData(student.id); }}
                          className="text-coral-500 hover:text-coral-600 font-semibold text-xs transition-colors duration-150"
                        >
                          Clear Data
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Mock Profiles */}
            <div>
              {mockProfiles.length === 0 ? (
                <div className="bg-gradient-to-br from-stone-100 to-white border-2 border-dashed border-stone-300 rounded-3xl py-12 px-8 text-center transition-all duration-200 hover:border-teal-400 hover:bg-gradient-to-br hover:from-teal-50 hover:to-white">
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-3">No custom profiles</h3>
                  <p className="text-stone-500 mb-8 leading-relaxed">Create custom student profiles for specific test scenarios</p>
                  <button
                    onClick={() => setShowCreateMock(true)}
                    className="bg-transparent border-2 border-teal-500 text-teal-600 px-6 py-3 rounded-xl font-semibold text-sm cursor-pointer transition-all duration-150 hover:bg-teal-500 hover:text-white hover:-translate-y-0.5"
                  >
                    Create Profile
                  </button>
                </div>
              ) : (
                <div className="space-y-2"> {/* reduced space-y-3 to space-y-2 */}
                  {mockProfiles.map(profile => (
                    <div key={profile.id} className="bg-white rounded-xl p-3 border border-slate-200"> {/* p-4 to p-3 */}
                      <div className="flex items-center justify-between mb-1"> {/* mb-2 to mb-1 */}
                        <div>
                          <h4 className="font-semibold text-slate-900 text-base">{profile.name}</h4> {/* text-base */}
                          <span className="text-xs text-slate-500">{profile.grade} • {profile.profileType}</span>
                        </div>
                        <div className="flex items-center gap-1"> {/* gap-2 to gap-1 */}
                          <button
                            onClick={() => handleSelectProfile(profile)}
                            className={`w-auto mx-auto flex items-center gap-1 px-3 py-1 rounded transition-colors text-xs focus:outline-none focus:ring-2 focus:ring-azure-400 focus:ring-offset-2 ${
                              selectedProfile?.id === profile.id 
                                ? 'bg-azure-100 text-azure-700 border border-azure-300' 
                                : 'bg-slate-900 text-white hover:bg-slate-800'
                            }`}
                            aria-label={selectedProfile?.id === profile.id ? 'Selected' : 'Select profile'}
                          >
                            {selectedProfile?.id === profile.id ? (
                              <>
                                <Eye className="w-3 h-3" />
                                Selected
                              </>
                            ) : (
                              <>
                                <User className="w-3 h-3" />
                                Select
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => deleteMockProfile(profile.id)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600">{profile.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Set College Preferences */}
        {selectedProfile && (
          <div className="mb-6" ref={step2Ref}> {/* add ref */}
            <h2 className="text-xl font-bold text-slate-900 mb-4">Step 2: Set College Preferences</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {/* Show auto-selected data if preference mode is already set */}
              {preferenceMode === 'saved' && selectedPreferenceSet && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-emerald-900">Auto-selected Preference Set</h4>
                        <p className="text-sm text-emerald-700 mt-1">{selectedPreferenceSet.name}</p>
                        {selectedPreferenceSet.description && (
                          <p className="text-xs text-emerald-600 mt-1">{selectedPreferenceSet.description}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setPreferenceMode(null);
                        setSelectedPreferenceSet(null);
                      }}
                      className="text-sm text-emerald-700 hover:text-emerald-800 font-medium underline"
                    >
                      Change
                    </button>
                  </div>
                </div>
              )}
              
              {!preferenceMode && (
                <>
                  <div className="mb-4">
                    <h3 className="font-semibold text-slate-900 mb-2">Choose preference setup method for {selectedProfile.displayName || selectedProfile.name}</h3>
                    <p className="text-sm text-slate-600">
                      {selectedProfile?.isRealProfile 
                        ? `Create or use saved mock testing data for ${selectedProfile.name}`
                        : 'You can create new preferences or use a saved preference set'
                      }
                    </p>
                    
                    {/* Show status of available mock data for real profiles */}
                    {selectedProfile?.isRealProfile && (
                      <div className="mt-3 p-3 bg-azure-50 border border-azure-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <TestTube className="w-4 h-4 text-azure-600" />
                          <span className="text-sm font-medium text-azure-900">
                            Mock Testing Data for {selectedProfile.name}:
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-azure-700">
                          • {savedPreferenceSets.length} saved preference sets
                          {savedPreferenceSets.length > 0 && (
                            <span className="text-azure-600"> - {savedPreferenceSets[0].name}</span>
                          )}
                          <br />
                          • {savedResponseSets.length} saved response sets
                          {savedResponseSets.length > 0 && (
                            <span className="text-azure-600"> - {savedResponseSets[0].name}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Create New Preferences */}
                    <button
                      onClick={() => {
                        setPreferenceMode('new');
                        setShowCollegePreferences(true);
                      }}
                      className="p-6 border-2 border-slate-200 rounded-xl hover:border-azure-500 hover:bg-azure-50 transition-all text-left"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Plus className="w-6 h-6 text-azure-600" />
                        <h4 className="font-semibold text-slate-900">Create New Preferences</h4>
                      </div>
                      <p className="text-sm text-slate-600">Set up custom college preferences for this test</p>
                    </button>
                    
                    {/* Use Saved Preference Set */}
                    <button
                      onClick={() => setPreferenceMode('saved')}
                      disabled={savedPreferenceSets.length === 0}
                      className="p-6 border-2 border-slate-200 rounded-xl hover:border-azure-500 hover:bg-azure-50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <FileText className="w-6 h-6 text-azure-600" />
                        <h4 className="font-semibold text-slate-900">Use Saved Preference Set</h4>
                      </div>
                      <p className="text-sm text-slate-600">
                        {savedPreferenceSets.length > 0 
                          ? `Choose from ${savedPreferenceSets.length} saved preference ${savedPreferenceSets.length === 1 ? 'set' : 'sets'}`
                          : 'No saved preference sets available'
                        }
                      </p>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Step 2a: Select Saved Preference Set */}
        {selectedProfile && preferenceMode === 'saved' && (
          <div className="mb-6"> {/* keep as is, will scroll to Step 2 above */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Step 2: Select Saved Preference Set</h2>
              <button
                onClick={() => {
                  setPreferenceMode(null);
                  setSelectedPreferenceSet(null);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-slate-600 hover:text-slate-900 text-sm"
              >
                <ArrowLeft className="w-3 h-3" />
                Back
              </button>
            </div>
            
            <div className="space-y-3">
              {savedPreferenceSets.map((prefSet) => (
                <div key={prefSet.id} className="bg-white rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-900">{prefSet.name}</h4>
                      {prefSet.description && (
                        <p className="text-sm text-slate-600 mt-1">{prefSet.description}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        Created {new Date(prefSet.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedPreferenceSet(prefSet)}
                      className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                        selectedPreferenceSet?.id === prefSet.id
                          ? 'bg-azure-100 text-azure-700 border border-azure-300'
                          : 'bg-slate-900 text-white hover:bg-slate-800'
                      }`}
                    >
                      {selectedPreferenceSet?.id === prefSet.id ? 'Selected' : 'Select'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Step 3: Use Response Set (Optional) */}
        {selectedProfile && (preferenceMode === 'saved' ? selectedPreferenceSet : (preferenceMode === 'new' && localStorage.getItem('mock-college-preferences'))) && (
          <div className="mb-6" ref={step3Ref}> {/* add ref */}
            <h2 className="text-xl font-bold text-slate-900 mb-4">Step 3: Use Response Set (Optional)</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="mb-4">
                <h3 className="font-semibold text-slate-900 mb-2">Pre-fill responses for faster testing</h3>
                <p className="text-sm text-slate-600">You can use saved response sets to skip answering questions</p>
              </div>
              
              {savedResponseSets.length === 0 ? (
                <div className="text-center py-4">
                  <MessageSquare className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">No saved response sets available</p>
                  <p className="text-xs text-slate-500 mt-1">Complete an assessment to save response sets</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedResponseSets.map((respSet) => {
                    const responseCount = Object.keys(respSet.responses || {}).length;
                    return (
                      <div key={respSet.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-900">{respSet.name}</h4>
                            <p className="text-sm text-slate-600 mt-1">
                              {responseCount} responses • {respSet.assessment_type}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedResponseSet?.id === respSet.id && (
                              <div className="flex items-center gap-2">
                                <label className="text-sm text-slate-600">Use responses:</label>
                                <select
                                  value={responseSetType || ''}
                                  onChange={(e) => setResponseSetType(e.target.value)}
                                  className="px-3 py-1 border border-slate-200 rounded text-sm"
                                >
                                  <option value="">Select range</option>
                                  <option value="1-8">Questions 1-8</option>
                                  <option value="1-15">Questions 1-15</option>
                                </select>
                              </div>
                            )}
                            <button
                              onClick={() => {
                                if (selectedResponseSet?.id === respSet.id) {
                                  setSelectedResponseSet(null);
                                  setResponseSetType(null);
                                } else {
                                  setSelectedResponseSet(respSet);
                                  setResponseSetType('1-8'); // Default to 1-8
                                }
                              }}
                              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                                selectedResponseSet?.id === respSet.id
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : 'bg-slate-900 text-white hover:bg-slate-800'
                              }`}
                            >
                              {selectedResponseSet?.id === respSet.id ? 'Clear' : 'Use'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {!selectedResponseSet && savedResponseSets.length > 0 && (
                <div className="text-center py-4 mb-4">
                  <p className="text-sm text-slate-600 mb-3">Or select a response set below:</p>
                </div>
              )}
              
              {/* API Request Preview */}
              <APIRequestPreview
                selectedProfile={selectedProfile}
                selectedResponseSet={selectedResponseSet}
                responseSetType={responseSetType}
                selectedPreferenceSet={selectedPreferenceSet}
                mockCollegePreferences={mockCollegePreferences}
                generateAPIRequestPreview={generateAPIRequestPreview}
              />
              
              <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-6">
                <div className="text-sm text-slate-600">
                  {selectedResponseSet 
                    ? `Using response set: ${selectedResponseSet.name} (${responseSetType})`
                    : 'No response set selected - you\'ll answer questions manually'
                  }
                </div>
                
                <button
                  onClick={handleStartAssessment}
                  className="flex items-center gap-2 px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors font-medium"
                >
                  <Play className="w-4 h-4" />
                  Start Assessment
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Step Selection Instructions */}
        {!selectedProfile && (
          <div className="mb-6">
            <div className="bg-slate-100 rounded-xl p-6 text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Select a Profile to Continue</h3>
              <p className="text-sm text-slate-600">Choose a real student profile or custom mock profile above to configure college preferences and start testing.</p>
            </div>
          </div>
        )}


        {/* Create Mock Profile Modal */}
        {showCreateMock && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setShowCreateMock(false)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Testing Hub
                </button>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Create Custom Mock Profile</h2>
              <p className="text-slate-600 text-sm mb-6">Design a student profile to test specific scenarios with the 6-phase assessment framework</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Student Name & Type</label>
                  <input
                    type="text"
                    value={newMockProfile.name}
                    onChange={(e) => setNewMockProfile({...newMockProfile, name: e.target.value})}
                    placeholder="e.g., Jordan (Art-Science Bridge), Alex (Quiet Leader), Sam (System Questioner)"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Grade Level</label>
                  <select
                    value={newMockProfile.grade}
                    onChange={(e) => setNewMockProfile({...newMockProfile, grade: e.target.value})}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  >
                    <option value="">Select grade</option>
                    <option value="9th">9th Grade</option>
                    <option value="10th">10th Grade</option>
                    <option value="11th">11th Grade</option>
                    <option value="12th">12th Grade</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">What Makes Them Unique</label>
                  <input
                    type="text"
                    value={newMockProfile.profileType}
                    onChange={(e) => setNewMockProfile({...newMockProfile, profileType: e.target.value})}
                    placeholder="e.g., Obsessed with linguistics, Questions authority, Creates art from trash"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Testing Scenario</label>
                  <textarea
                    value={newMockProfile.description}
                    onChange={(e) => setNewMockProfile({...newMockProfile, description: e.target.value})}
                    placeholder="What specific scenario do you want to test? E.g., 'A student who struggles with traditional assessments but has deep expertise in unusual areas' or 'Someone who challenges rules when they conflict with their values'"
                    rows={4}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
                  <h4 className="font-medium text-azure-900 mb-2">💡 Testing Ideas</h4>
                  <ul className="text-sm text-azure-800 space-y-1">
                    <li>• Edge cases: Students who don't fit traditional molds</li>
                    <li>• Quirky learners: Deep expertise in unusual areas</li>
                    <li>• System challengers: Question institutional logic</li>
                    <li>• Bridge builders: Connect seemingly unrelated fields</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateMock(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMockProfile}
                  disabled={!newMockProfile.name || !newMockProfile.grade}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Profile
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mock College Preferences Modal */}
        {showCollegePreferences && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setShowCollegePreferences(false)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Testing Hub
                </button>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Mock College Preferences</h2>
              <p className="text-slate-600 text-sm mb-6">
                Set mock college preferences{selectedProfile ? ` for ${selectedProfile.displayName || selectedProfile.name}` : ''} to test the AI synthesis
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Target Schools</label>
                  <input
                    type="text"
                    value={mockCollegePreferences.targetSchools}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, targetSchools: e.target.value})}
                    placeholder="e.g., MIT, Stanford, Georgia Tech, Carnegie Mellon"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Preferred Majors</label>
                  <input
                    type="text"
                    value={mockCollegePreferences.preferredMajors}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, preferredMajors: e.target.value})}
                    placeholder="e.g., Computer Science, Engineering, Mathematics"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Location Preferences</label>
                  <input
                    type="text"
                    value={mockCollegePreferences.locationPreferences}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, locationPreferences: e.target.value})}
                    placeholder="e.g., West Coast, No preference, Urban setting"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Campus Size Preference</label>
                  <select
                    value={mockCollegePreferences.campusSize}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, campusSize: e.target.value})}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  >
                    <option value="">Select preference</option>
                    <option value="Small (under 5,000)">Small (under 5,000)</option>
                    <option value="Medium (5,000-15,000)">Medium (5,000-15,000)</option>
                    <option value="Large (over 15,000)">Large (over 15,000)</option>
                    <option value="No preference">No preference</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Special Interests</label>
                  <textarea
                    value={mockCollegePreferences.specialInterests}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, specialInterests: e.target.value})}
                    placeholder="e.g., Research opportunities, Study abroad, Entrepreneurship programs"
                    rows={3}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Career Goals</label>
                  <textarea
                    value={mockCollegePreferences.careerGoals}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, careerGoals: e.target.value})}
                    placeholder="e.g., Software engineering, Research, Starting a tech company"
                    rows={3}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Most Important Factors</label>
                  <textarea
                    value={mockCollegePreferences.collegeImportanceFactors}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, collegeImportanceFactors: e.target.value})}
                    placeholder="e.g., Academic reputation, Research opportunities, Campus culture"
                    rows={3}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Concerns or Worries</label>
                  <textarea
                    value={mockCollegePreferences.concernsOrWorries}
                    onChange={(e) => setMockCollegePreferences({...mockCollegePreferences, concernsOrWorries: e.target.value})}
                    placeholder="e.g., Competition level, Social fit, Financial concerns"
                    rows={3}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCollegePreferences(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveMockCollegePreferences}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
                >
                  Save Mock Preferences
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Preference Set Modal */}
        {showSavePreferenceOption && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-2">Save Preference Set</h2>
              <p className="text-slate-600 text-sm mb-6">
                Would you like to save these preferences as a reusable set for future tests?
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Preference Set Name
                  </label>
                  <input
                    type="text"
                    value={preferenceName}
                    onChange={(e) => setPreferenceName(e.target.value)}
                    placeholder="e.g., Engineering Schools West Coast"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowSavePreferenceOption(false);
                    setPreferenceName('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Skip
                </button>
                <button
                  onClick={savePreferenceSet}
                  disabled={!preferenceName.trim()}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Preference Set
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mock Data Manager Modal */}
        {showMockDataManager && (
          <MockDataManager
            onClose={() => setShowMockDataManager(false)}
            onApplyMockData={(type, data) => {
              console.log(`Applied mock ${type}:`, data);
              // Optionally refresh the page or update UI to reflect applied data
            }}
            selectedStudentId={selectedProfile?.id}
          />
        )}
      </div>
    </div>
  );
};

export default ParentTestingHub;